import { useCallback, useEffect, useRef, useState } from 'react';
import { AiInterviewSession } from '../types';
import { LiveSessionEvents, LiveConnectionState, LiveSessionDriver } from './useGeminiLiveSession';

export interface MockLiveSessionDriver extends LiveSessionDriver {
  /** Sends a simulated spoken answer (typed into the prompt box). */
  submitAnswer: (text: string) => void;
  isMock: true;
}

const MOCK_QUESTIONS = [
  "Tell me about yourself and why you are interested in this role.",
  "Walk me through a project you are proud of. What was your specific contribution?",
  "Describe a technical challenge you faced and how you solved it.",
  "How do you prioritize your tasks when working under a tight deadline?",
  "Why do you want to work at this company, and where do you see yourself in two years?",
];

const MAX_MOCK_MILLIS = 5 * 60 * 1000;
const MOCK_CONCLUSION =
  'That was a great session — thank you for your answers and best of luck with the application!';

/**
 * Scripted stand-in for the real Gemini Live session. Used only when
 * VITE_INTERVIEW_MOCK_MODE=true in development builds.
 *
 * The interviewer asks a question aloud (simulated via transcription events)
 * and then waits for the user to type a simulated spoken answer. The same event
 * callbacks are driven, so the whole page flow — transcript, stage machine,
 * persistence and completion — works identically to the live path.
 */
export function useMockLiveSession(
  session: AiInterviewSession | null,
  events: LiveSessionEvents
): MockLiveSessionDriver {
  const [state, setState] = useState<LiveConnectionState>('idle');
  const [error] = useState<string | null>(null);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);

  const sessionRef = useRef(session);
  const eventsRef = useRef(events);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  const timersRef = useRef<Array<number>>([]);
  const stoppedRef = useRef(true);
  const questionIndexRef = useRef(0);
  const hardStopAtRef = useRef(0);

  const later = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(fn, ms);
    timersRef.current.push(id);
  }, []);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  const askQuestion = useCallback(() => {
    if (stoppedRef.current) return;
    const text = MOCK_QUESTIONS[questionIndexRef.current % MOCK_QUESTIONS.length];
    setIsAiSpeaking(true);
    eventsRef.current.onTranscription('AI', text, false);
    later(() => {
      if (stoppedRef.current) return;
      setIsAiSpeaking(false);
      eventsRef.current.onTranscription('AI', text, true);
      eventsRef.current.onTurnComplete();
    }, 400);
  }, [later]);

  const start = useCallback(async () => {
    const current = sessionRef.current;
    if (!current) return;

    stoppedRef.current = false;
    questionIndexRef.current = 0;
    hardStopAtRef.current = Date.now() + MAX_MOCK_MILLIS;
    setIsAiSpeaking(false);

    setState('connecting');
    later(() => {
      if (stoppedRef.current) return;
      setState('open');
      eventsRef.current.onOpen();
      const intro =
        current.systemInstruction ||
        'Mock assistant: I will interview you now. Answer out loud — or type a simulated answer below.';
      const picked = intro.split(/\n/).find((line) => line.trim().length > 0) ?? intro;
      setIsAiSpeaking(true);
      eventsRef.current.onTranscription('AI', picked.trim(), false);
      later(() => {
        if (stoppedRef.current) return;
        setIsAiSpeaking(false);
        eventsRef.current.onTranscription('AI', picked.trim(), true);
        eventsRef.current.onTurnComplete();
        later(() => askQuestion(), 900);
      }, 350);
    }, 800);
  }, [askQuestion, later]);

  const submitAnswer = useCallback(
    (rawText: string) => {
      const text = rawText.trim();
      if (!text) return;
      if (stoppedRef.current) return;
      if (state !== 'open') return;
      if (Date.now() > hardStopAtRef.current) return;

      setIsAiSpeaking(false);
      eventsRef.current.onTranscription('CANDIDATE', text, false);
      later(() => {
        if (stoppedRef.current) return;
        eventsRef.current.onTranscription('CANDIDATE', text, true);
        eventsRef.current.onTurnComplete();
        later(() => {
          questionIndexRef.current += 1;
          askQuestion();
        }, 1100);
      }, 350);
    },
    [askQuestion, later, state]
  );

  const stop = useCallback(async () => {
    stoppedRef.current = true;
    clearTimers();
    setIsAiSpeaking(false);
    setState('closed');
  }, [clearTimers]);

  const signalWrapUp = useCallback(() => {
    if (stoppedRef.current) return;
    clearTimers();
    setIsAiSpeaking(true);
    eventsRef.current.onTranscription('AI', MOCK_CONCLUSION, false);
    later(() => {
      if (stoppedRef.current) return;
      setIsAiSpeaking(false);
      eventsRef.current.onTranscription('AI', MOCK_CONCLUSION, true);
      eventsRef.current.onTurnComplete();
    }, 400);
  }, [clearTimers, later]);

  useEffect(
    () => () => {
      stoppedRef.current = true;
      clearTimers();
      setIsAiSpeaking(false);
    },
    [clearTimers]
  );

  return {
    state,
    error,
    micOn: false,
    micError: null,
    isAiSpeaking,
    start,
    stop,
    signalWrapUp,
    submitAnswer,
    isMock: true,
  };
}